import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Eye, ArrowRight, Rss } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/blog/posts?limit=20`)
      .then(r => r.json())
      .then(data => {
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      })
      .catch(err => console.error('Error fetching blog posts:', err))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return 'Recent';
    }
  };

  const extractExcerpt = (htmlContent) => {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    return (div.textContent || '').substring(0, 180) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#5c0701] to-[#8b1a15] text-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">CT Remodel News</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-6">
            Expert insights, tips, and trends for Connecticut homeowners looking to transform their homes
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span>{total} Articles</span>
            <span>|</span>
            <a href="/blog/rss" className="flex items-center gap-1 hover:text-white transition">
              <Rss className="w-4 h-4" /> RSS Feed
            </a>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5c0701] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading articles...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg mb-4">No blog posts yet. Check back soon!</p>
            <Link to="/" className="text-[#3a87fe] hover:underline">Return to Home</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <article 
                key={post.id || post.slug} 
                className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col"
                data-testid={`blog-post-${index}`}
              >
                {/* Category Badge */}
                <div className="p-5 pb-0">
                  <span className="inline-block bg-[#5c0701]/10 text-[#5c0701] px-3 py-1 rounded-full text-xs font-medium">
                    {post.category || 'Home Remodeling'}
                  </span>
                </div>
                
                {/* Content */}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(post.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" />
                      {post.views || 0} views
                    </span>
                  </div>
                  
                  <h2 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 leading-tight">
                    {post.title}
                  </h2>
                  
                  <p className="text-sm text-gray-600 mb-4 flex-1 line-clamp-3">
                    {post.excerpt || extractExcerpt(post.content)}
                  </p>
                  
                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {post.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <Link 
                    to={`/blog/${post.slug}`} 
                    className="mt-auto inline-flex items-center gap-2 text-[#3a87fe] font-medium text-sm hover:text-[#2968d9] transition group"
                  >
                    Read Article 
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Newsletter CTA */}
      <section className="bg-gradient-to-r from-[#3a87fe] to-[#2968d9] py-12 px-4">
        <div className="max-w-2xl mx-auto text-center text-white">
          <h2 className="text-2xl font-bold mb-3">Stay Updated</h2>
          <p className="text-white/90 mb-6">Get the latest home remodeling tips and insights delivered to your inbox.</p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:ring-2 focus:ring-white"
            />
            <button className="bg-white text-[#3a87fe] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
